//////////////////
// IMPORTS
//////////////////
// schema
// state
import {
  setActiveViewDay,
  setActiveViewFeed,
} from "#webapp/src/state/actions/chrome-extension.actions";
import {
  setSelectedMoment,
  setSnapToSelectedMomentOnNextMount,
} from "#webapp/src/state/actions/day-view.actions";
import {
  makeEventSchedulerViewActive,
  updateEventSchedulerHasUnsavedChanges,
  updateFeedSchedulerInput,
  updateScheduledEventIds,
} from "#webapp/src/state/actions/feed-scheduler.actions";
// material-ui
import { useMutation } from "@apollo/client";
// util
import { eventFromParent } from "@clockwise/client-commons/src/util/event";
import { IAutopilotSuggestionOptions } from "@clockwise/client-commons/src/util/event-tag";
import { Button, TextField } from "@clockwise/design-system";
import {
  ConferenceTypeEnum,
  ScheduleNewEventV2MutationInput,
  UpdateFlexibilityInput,
} from "@clockwise/schema";
import {
  MakeItWorkCard,
  isMakeItWorkSlot,
} from "@clockwise/web-commons/src/components/make-it-work/MakeItWork";
import { Day } from "@clockwise/web-commons/src/ui/autopilot-flexibility/AutopilotFlexibility.types";
import { SetEventFlexibilityDocument } from "@clockwise/web-commons/src/ui/autopilot-flexibility/__generated__/SetEventFlexibility.generated";
import {
  defaultDayOnOffMap,
  getDaysOfWeekFromDayOnOffMap,
} from "@clockwise/web-commons/src/ui/working-hours";
// other imports
import { AutopilotFormSidebarScheduler } from "#webapp/src/components/autopilot-form";
import { EventSchedulerAttendeeInput } from "#webapp/src/components/event-scheduler-attendee-input";
import { EventSchedulerDescriptionInput } from "#webapp/src/components/event-scheduler-description-input";
import { EventSchedulerLocationInput } from "#webapp/src/components/event-scheduler-location-input";
import { EventSchedulerRoomInputRenderer } from "#webapp/src/components/event-scheduler-room-input";
import { EventSchedulerTimeInputRenderer } from "#webapp/src/components/event-scheduler-time-input";
import { EventSchedulerVideoConferencingInput } from "#webapp/src/components/event-scheduler-video-conferencing-input";
import { Divider, FormControl, FormHelperText } from "@material-ui/core";
// mutation
import * as ISchema from "#webapp/src/__schema__";
import {
  DayOfWeek,
  DayRangeFlexibilityType,
  RoomChangePolicy,
  UpdateTimeOfDayTimeSlot,
} from "#webapp/src/__schema__";
import { scheduleNewEvent } from "#webapp/src/mutations";
import { initialFeedSchedulerState } from "#webapp/src/state/reducers/feed-scheduler.reducer";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { TrackingEvents, track } from "#webapp/src/util/analytics.util";
import { EFlex } from "#webapp/src/util/event-tag.util";
import { getRoomsFromAttendees, maybeGetWarning } from "#webapp/src/util/event.util";
import { logger } from "#webapp/src/util/logger.util";
import { getCurrentOrg } from "#webapp/src/util/org.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { EScheduleRange, getMaxMinSlotForRange } from "#webapp/src/util/scheduler.util";
import { isBestTime, isSoonTime } from "#webapp/src/util/scheduling-slot.util";
import { ZonedMoment, getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { isEmpty, keys } from "lodash";
// libraries
import moment from "moment-timezone";
import * as React from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { createRefetchContainer } from "react-relay";
// event-scheduler imports
import { useMonetization } from "#webapp/src/hooks/useMonetization";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
import toast from "react-hot-toast";
import { setHighlightedSlot, setMeetingTitle } from "../../state/actions/planner-highlight.actions";
import { ChromeNavEventScheduler } from "../chrome-nav/nav-content";
import { eventSchedulerFragments, eventSchedulerQuery } from "./EventScheduler.gql";
import * as s from "./EventScheduler.styles";
import { IContainer, IProps, IState, RoomOptionEnum } from "./EventSchedulerTypes";

// thoughts:
// This will be a wrapper component that will maintain all the event info state
// each of the subcomponents will work as dumb input components where
// they pass back the values that are selected on an onChange component
// also this component will have to do some validation checks on change
// because changing one attribute of an event could invalidate other
// things(eg. changing duration could affect room availability)

//////////////////
// COMPONENT
//////////////////
const EventSchedulerCmpt = (props: IProps) => {
  const {
    viewer,
    relay,
    updateEventSchedulerHasUnsavedChanges,
    schedulingSessionId,
    makeEventSchedulerViewActive,
    setActiveViewFeed,
    setSelectedMoment,
    setSnapToSelectedMomentOnNextMount,
    updateScheduledEventIds,
    updateFeedSchedulerInput,
    onAttendeeChange,
    isWebExtension,
  } = props;
  const dispatch = useDispatch();
  const ecosystem = useEcosystem();
  const isChromeExtension = useSelector((state: IReduxState) => state.webExtension.isWebExtension);
  const { canUserAccessFlexMeetings } = useMonetization();
  const getZoomAccountFromOrg = (props: IProps) => {
    const org = getCurrentOrg(props.viewer);
    return (
      (org && org.zoomAccount && org.zoomAccount.__typename === "ZoomAccount" && org.zoomAccount) ||
      undefined
    );
  };

  const getZoomUserLevelAccountFromOrg = (props: IProps) => {
    const org = getCurrentOrg(props.viewer);
    return (
      (org &&
        org.zoomUserLevelAccount &&
        org.zoomUserLevelAccount.__typename === "ZoomAccount" &&
        org.zoomUserLevelAccount) ||
      undefined
    );
  };

  const generateState = (props: IProps): IState => {
    // set intial event options from feed-scheduler
    // TODO: (maybe better to have chrome-wrapper connected to redux and just pass these in through props for modular reasons)
    const org = getCurrentOrg(props.viewer);
    const userCalId = org && org.primaryOrgCalendar;

    let calendarIds =
      userCalId && props.calendarIds.indexOf(userCalId) === -1
        ? [userCalId, ...props.calendarIds]
        : props.calendarIds;
    let includeRoom = props.roomRequired ? RoomOptionEnum.WithRoom : RoomOptionEnum.WithoutRoom;
    let minutesDuration = props.minutesDuration;
    let range = props.range || EScheduleRange.NextSeven;
    let specifiedDate = props.specifiedDate || null;

    // if we have a rescheduler event, set state from it
    if (props.reschedulerEvent) {
      const ev = props.reschedulerEvent;
      const evDate = new Date(props.reschedulerEvent.startTime.dateOrDateTime);
      calendarIds = ev.calendarIds;
      includeRoom = RoomOptionEnum.WithoutRoom;
      minutesDuration = Math.round((ev.endTime.millis - ev.startTime.millis) / 60000);
      if (evDate > new Date()) {
        range = EScheduleRange.AroundDate;
        specifiedDate = evDate;
      }

      const rooms = getRoomsFromAttendees(ev.attendees);
      if (rooms.length) {
        includeRoom = RoomOptionEnum.SpecificRoom;
      }
    }

    const flex =
      range === EScheduleRange.ThisWeek ||
      range === EScheduleRange.NextSeven ||
      range === EScheduleRange.NextWeek
        ? EFlex.Week
        : EFlex.Day;

    const maybeZoomAccount = getZoomAccountFromOrg(props);
    const maybeZoomUserLevelAccount = getZoomUserLevelAccountFromOrg(props);
    const canUseZoom =
      maybeZoomUserLevelAccount || (maybeZoomAccount && maybeZoomAccount.hasValidZoomAccount);

    return {
      calendarIds,
      includeRoom,
      minutesDuration,
      range,
      specifiedDate,
      title: "",
      roomIds: [],
      description: "",
      location: "",
      includeZoomLink: !!canUseZoom,
      selectedSlot: undefined,
      savingEvent: false,
      openingInGCal: false,
      focusEventTitle: false,
      titleError: "",
      timeError: "",
      uncrawledCalendarsCount: 0,
      openRoomInput: false,
      useAutoSchedule: props.useAutoSchedule,
      flex: !maybeGetWarning(props.viewer, calendarIds) ? flex : EFlex.Pin,
      allowSimilarRooms: false,
      conflictResolutionDefragId: undefined,
      allowedStart: null,
      allowedEnd: null,
    };
  };

  const generatedStateFromProps = React.useMemo(() => generateState(props), [
    // Since we only use this once, only generated it once
  ]);

  const [title, setTitle] = React.useState(generatedStateFromProps.title);
  const [calendarIds, setCalendarIds] = React.useState(generatedStateFromProps.calendarIds);
  const [minutesDuration, setMinutesDuration] = React.useState(
    generatedStateFromProps.minutesDuration,
  );
  const [includeRoom, setIncludeRoom] = React.useState(generatedStateFromProps.includeRoom);
  const [range, setRange] = React.useState(generatedStateFromProps.range);
  const [specifiedDate, setSpecifiedDate] = React.useState(generatedStateFromProps.specifiedDate);
  const [roomIds, setRoomIds] = React.useState(generatedStateFromProps.roomIds);
  const [description, setDescription] = React.useState(generatedStateFromProps.description);
  const [location, setLocation] = React.useState(generatedStateFromProps.location);
  const [conferenceType, setConferenceType] = React.useState<ConferenceTypeEnum | null>(
    generatedStateFromProps.includeZoomLink
      ? ConferenceTypeEnum.Zoom
      : ecosystem === "Google"
      ? ConferenceTypeEnum.Meet
      : ConferenceTypeEnum.TeamsForBusiness,
  );

  const [selectedSlot, setSelectedSlot] = React.useState(generatedStateFromProps.selectedSlot);
  const [savingEvent, setSavingEvent] = React.useState(generatedStateFromProps.savingEvent);
  const [openingInGCal, setOpeningInGCal] = React.useState(generatedStateFromProps.openingInGCal);
  const [focusEventTitle, setFocusEventTitle] = React.useState(
    generatedStateFromProps.focusEventTitle,
  );
  const [titleError, setTitleError] = React.useState(generatedStateFromProps.titleError);
  const [timeError, setTimeError] = React.useState(generatedStateFromProps.timeError);
  const [uncrawledCalendarsCount, setUncrawledCalendarsCount] = React.useState(
    generatedStateFromProps.uncrawledCalendarsCount,
  );
  const [openRoomInput, setOpenRoomInput] = React.useState(generatedStateFromProps.openRoomInput);
  const [useAutoSchedule, setUseAutoSchedule] = React.useState(
    generatedStateFromProps.useAutoSchedule,
  );
  const [flex, setFlex] = React.useState(generatedStateFromProps.flex);
  const [dayOnOffMap, setDayOnOffMap] = React.useState(defaultDayOnOffMap);
  const daysOfWeek = React.useMemo(() => {
    return getDaysOfWeekFromDayOnOffMap(dayOnOffMap);
  }, [dayOnOffMap]);

  const [allowSimilarRooms, setAllowSimilarRooms] = React.useState(
    generatedStateFromProps.allowSimilarRooms,
  );
  const [conflictResolutionDefragId, setConflictResolutionDefragId] = React.useState(
    generatedStateFromProps.conflictResolutionDefragId,
  );
  const [allowedStart, setAllowedStart] = React.useState(generatedStateFromProps.allowedStart);
  const [allowedEnd, setAllowedEnd] = React.useState(generatedStateFromProps.allowedEnd);
  const [recurrenceOption, __] = React.useState(generatedStateFromProps?.recurrenceOption);
  const [shouldValidateAndMaybeSetState, setShouldValidateAndMaybeSetState] = React.useState(false);

  const eventSchedulerContainer = React.useRef<HTMLDivElement | null>(null);

  const [setEventFlexibilityMutation] = useMutation(SetEventFlexibilityDocument);

  const setEventFlexibility = ({
    externalEventId,
    calendarId,
    selectedFlex,
    selectedDaysOfWeek,
    allowedEnd,
    allowedStart,
    roomChangePolicy,
  }: {
    externalEventId: string;
    calendarId: string;
    selectedFlex: EFlex;
    selectedDaysOfWeek: DayOfWeek[];
    allowedEnd: UpdateTimeOfDayTimeSlot;
    allowedStart: UpdateTimeOfDayTimeSlot;
    roomChangePolicy: RoomChangePolicy;
  }) => {
    if (selectedFlex === EFlex.Pin) {
      // No needs to set flexibility
      return;
    }

    const org = getCurrentOrg(viewer);
    if (!org) {
      return;
    }
    const orgRelayId = org.id;

    const input: UpdateFlexibilityInput = {
      orgRelayId: orgRelayId,
      eventUpdates: [
        {
          eventId: externalEventId,
          dayRangeFlexibility: {
            type: (selectedFlex as unknown) as DayRangeFlexibilityType,
            allowedDays: selectedFlex == EFlex.DayOfWeek ? selectedDaysOfWeek : undefined,
          },
          timeOfDayFlexibility: {
            allowedEnd: allowedEnd ?? UpdateTimeOfDayTimeSlot.Unbounded,
            allowedStart: allowedStart ?? UpdateTimeOfDayTimeSlot.Unbounded,
          },
          roomFlexibility: {
            roomChangePolicy,
          },
        },
      ],
    };

    void setEventFlexibilityMutation({
      variables: {
        input: input,
        CalendarId: calendarId,
      },
      onError: () => {
        toast.error("Event flexibility errored. Please try to set your flexibility again.");
      },
    });
  };

  const [showMakeItWorkCard, setShowMakeItWorkCard] = React.useState(false);
  const selectedSlotIsMakeItWorkSlot = isMakeItWorkSlot(selectedSlot);
  const [userClickedFixItNow, setUserClickedFixItNow] = React.useState(false);

  // constructor
  React.useEffect(() => {
    updateFeedSchedulerInput(
      initialFeedSchedulerState.calendarIds,
      initialFeedSchedulerState.roomRequired,
      initialFeedSchedulerState.useAutoSchedule,
      initialFeedSchedulerState.minutesDuration,
      initialFeedSchedulerState.range,
      initialFeedSchedulerState.specifiedDate,
    );
  }, []);

  // also from constructor
  React.useEffect(() => {
    relay.refetch(
      {
        calendarIds: calendarIds,
      },
      undefined,
      () => {
        const org = getCurrentOrg(viewer);

        // prefill title if there are two attendees
        if (
          org &&
          org.personsByCalendarIdsErrorable.__typename === "PersonList" &&
          org.personsByCalendarIdsErrorable.list.length === 2
        ) {
          const { list } = org.personsByCalendarIdsErrorable;
          const personAName = list[0].profile ? list[0].profile.givenName : null;
          const personBName = list[1].profile ? list[1].profile.givenName : null;

          if (personAName && personBName) {
            setTitle(`${personAName} // ${personBName}`);
          }
        }
      },
    );
  }, [viewer]);

  // componentDidMount
  React.useEffect(() => {
    if (calendarIds.length > 1) {
      updateEventSchedulerHasUnsavedChanges(true);
    }
  }, []);

  React.useEffect(() => {
    dispatch(setMeetingTitle(title));
  }, [title]);

  // validateAndMaybeSetState
  // We must handle `validateAndMaybeSetState` in a useEffect because of the setState callback
  React.useEffect(() => {
    if (!shouldValidateAndMaybeSetState) {
      return;
    }

    setTimeout(() => setOpenRoomInput(false), 300); // reset this state otherwise room-input will not be able to open again
    updateEventSchedulerHasUnsavedChanges(true); // make sure i set to false any place i need to
    setShouldValidateAndMaybeSetState(false);
  }, [shouldValidateAndMaybeSetState]);

  const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const title = event.target.value;

    setTitle(title);
    setTitleError("");
    setShouldValidateAndMaybeSetState(true);
  };

  const org = getCurrentOrg(viewer);
  if (!org) {
    logger.error("Failed to load event-scheduler");
    return <span />;
  }
  let persons: ISchema.IPerson[] = [];
  if (org.personsByCalendarIdsErrorable.__typename === "PersonList") {
    persons = org.personsByCalendarIdsErrorable.list;
  }

  const specifiedMoment = specifiedDate ? moment(specifiedDate) : undefined;
  const { rangeMin, rangeMax } = getMaxMinSlotForRange(range, specifiedMoment);

  const invitees = calendarIds.map((calendarId) => ({ calendarId, required: true }));
  // If the query has no attendees, or single attendee of just the current user, declare this as a TaskHold.
  const eventTraits = [];
  if (invitees.filter((invitee) => invitee.calendarId !== org.primaryOrgCalendar).length === 0) {
    eventTraits.push(ISchema.EventTrait.TaskHold);
  }

  const schedulerQueryInput = {
    orgId: org.id,
    environment: relay.environment,
    invitees,
    minutesDuration: minutesDuration,
    timeZone: getRenderTimeZone(),
    minSlot: rangeMin,
    maxSlot: rangeMax,
    includeRoom: includeRoom,
    roomsOverride: includeRoom === RoomOptionEnum.SpecificRoom ? roomIds : [],
    limit: 10,
    seenSlots: [],
    traits: eventTraits,
  };

  const handleAttendeeChange = (calendarIds: string[]) => {
    onAttendeeChange?.(calendarIds);

    // check if any new calendars are bad for autopilot
    const flexWarning = maybeGetWarning(viewer, calendarIds);
    const org = getCurrentOrg(viewer);

    setCalendarIds(calendarIds);
    setFocusEventTitle(true);
    flexWarning && setFlex(EFlex.Pin);
    setShouldValidateAndMaybeSetState(true);

    // refetch persons
    relay.refetch({ calendarIds: [org?.primaryOrgCalendar, ...calendarIds] });
  };

  const onTimeChange = (
    minutesDuration: number,
    includeRoom: RoomOptionEnum,
    range: EScheduleRange,
    specifiedDate: Date | null,
    uncrawledCalendarsCount: number,
    selectedSlot?: ISchema.ISchedulingSlot,
    specificRoomClicked?: boolean,
    conflictResolutionDefragId?: string,
  ) => {
    setMinutesDuration(minutesDuration);
    setIncludeRoom(includeRoom);
    setRange(range);
    setSpecifiedDate(specifiedDate);
    setUncrawledCalendarsCount(uncrawledCalendarsCount);
    setSelectedSlot(selectedSlot);
    setUseAutoSchedule(false);
    setConflictResolutionDefragId(conflictResolutionDefragId);
    setTimeError(selectedSlot ? "" : timeError);
    includeRoom === RoomOptionEnum.WithoutRoom && setRoomIds([]);
    setOpenRoomInput(!!specificRoomClicked);
    setShouldValidateAndMaybeSetState(true);
  };

  const onRoomChange = (roomIds: string[]) => {
    setRoomIds(roomIds);
    setShouldValidateAndMaybeSetState(true);
  };

  const onChangeConferenceType = (conferenceType: ConferenceTypeEnum | null) => {
    setConferenceType(conferenceType);
    setShouldValidateAndMaybeSetState(true);
  };

  const onAutopilotChange = ({
    flex,
    allowSimilarRooms,
    timeRange,
    daysOfWeek,
  }: IAutopilotSuggestionOptions & {
    daysOfWeek?: Day[];
  }) => {
    setFlex(flex);
    setAllowSimilarRooms(!!allowSimilarRooms);
    setAllowedStart(timeRange?.allowedStart ?? null);
    setAllowedEnd(timeRange?.allowedEnd ?? null);

    if (!isEmpty(daysOfWeek)) {
      const newDaysOnOffMap = {
        ...dayOnOffMap,
      };
      keys(newDaysOnOffMap).forEach((day) => {
        newDaysOnOffMap[day as Day] = false;
      });
      daysOfWeek?.forEach((day) => {
        newDaysOnOffMap[day] = true;
      });

      setDayOnOffMap(newDaysOnOffMap);
    }

    track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_TOGGLE_AUTOPILOT_SETTING, {
      flex,
      allowSimilarRooms,
      schedulingSessionId: schedulingSessionId,
      ...timeRange,
    });
  };

  const maybeRenderVideoConferencingInput = () => {
    const zoomAccount = getZoomAccountFromOrg(props);
    const zoomUserLevelAccount = getZoomUserLevelAccountFromOrg(props);

    return (
      <React.Fragment>
        <EventSchedulerVideoConferencingInput
          onChange={onChangeConferenceType}
          zoomAccount={zoomAccount}
          ecosystem={ecosystem}
          conferenceType={conferenceType}
          zoomUserLevelAccount={zoomUserLevelAccount}
        />
        <Divider style={s.divider} />
      </React.Fragment>
    );
  };

  const onDescriptionChange = (description: string) => {
    setDescription(description);
    setShouldValidateAndMaybeSetState(true);
  };

  const onLocationChange = (location: string) => {
    setLocation(location);
    setShouldValidateAndMaybeSetState(true);
  };

  const validateForm = () => {
    const errorMap = {
      error: false,
      titleError: "",
      timeError: "",
    };

    if (!title) {
      errorMap.error = true;
      errorMap.titleError = "Meeting title is required";
    }
    if (!selectedSlot) {
      errorMap.error = true;
      errorMap.timeError = "Meeting time is required";
    }

    return errorMap;
  };

  const setAutopilotStatus = (eventParent: ISchema.IEventParent) => {
    const org = getCurrentOrg(viewer);
    if (!org) {
      return logger.error("Failed to get org to set autopilot status");
    }

    const roomChangePolicy = allowSimilarRooms
      ? RoomChangePolicy.CanChangeRooms
      : RoomChangePolicy.CannotChangeRooms;

    setEventFlexibility({
      externalEventId: eventParent.externalEventId,
      calendarId: org?.primaryOrgCalendar,
      selectedFlex: flex,
      selectedDaysOfWeek: (daysOfWeek as unknown) as DayOfWeek[], // FIXME DayOfWeek and Day are duplicate enums, they should be merged
      allowedEnd: (allowedEnd?.timeSlot! as unknown) as UpdateTimeOfDayTimeSlot, // Check this works
      allowedStart: (allowedStart?.timeSlot! as unknown) as UpdateTimeOfDayTimeSlot, // Check this works
      roomChangePolicy,
    });
  };

  const onScheduleFail = (err: any) => {
    logger.error("Failed to schedule event via EventScheduler", err);
    toast.error("Sorry, something went wrong. Please try again.");

    dispatch(setHighlightedSlot(null));
    setSavingEvent(false);
    setOpeningInGCal(false);
  };

  const onScheduleSuccess = (
    payload?: ISchema.IScheduleNewEventV2MutationPayload,
    goToEdit?: boolean,
    calendarId?: string,
  ) => {
    setSavingEvent(false);
    setOpeningInGCal(false);
    onAttendeeChange?.([]); // reset attendees
    dispatch(setHighlightedSlot(null));

    const trackingEvent = !goToEdit
      ? TrackingEvents.SCHEDULING.EVENT_SCHEDULER_MEETING_SCHEDULED
      : TrackingEvents.SCHEDULING.EVENT_SCHEDULER_EDIT_IN_GCAL;
    track(trackingEvent, {
      schedulingSessionId: schedulingSessionId,
      externalEventId: payload && payload.eventParent.externalEventId,
      scheduledOn: new Date().valueOf(),
      isBestTime: selectedSlot ? isBestTime(selectedSlot) : false,
      isSoonestTime: selectedSlot ? isSoonTime(selectedSlot) : false,
      numOfAttendees: calendarIds.length,
      hasConferenceRoom: roomIds.length > 0,
      isBestRoom: selectedSlot
        ? roomIds.indexOf(selectedSlot.summary.availableRooms[0]) !== -1 // assume best if first room is selected
        : false,
      description: description,
      location: location,
      uncrawledCalendarsCount: uncrawledCalendarsCount,
      hasConflictingAttendees: selectedSlot
        ? selectedSlot.summary.conflictingAttendees.length > 0
        : false,
      conflictResolutionDefragId: conflictResolutionDefragId,
    }); // analytics

    if (payload && !maybeGetWarning(viewer, calendarIds) && canUserAccessFlexMeetings) {
      setAutopilotStatus(payload.eventParent);
    }

    updateEventSchedulerHasUnsavedChanges(false); // we have saved the changes so dont pop up unsaved changes modal

    if (!isChromeExtension) {
      toast.success("Your event has been scheduled! (You may need to wait and refresh)"); // for the webapp planner
    }

    if (goToEdit && payload && calendarId) {
      toast.success("Your event has been scheduled!"); // only show snack bar when going back to edit
      makeEventSchedulerViewActive(false);
      PostMessageManager.goToEventEditPage(payload.eventParent.externalEventId, calendarId);
    } else if (!goToEdit && payload && calendarId) {
      updateScheduledEventIds(payload.eventParent.externalEventId, calendarId); // store in redux for feed-scheduler
      makeEventSchedulerViewActive(false);
      setActiveViewFeed();
      const event = payload.eventParent && eventFromParent(payload.eventParent);
      if (event && event.startTime && event.startTime.millis) {
        setSelectedMoment(new ZonedMoment(event.startTime.millis));
        setSnapToSelectedMomentOnNextMount(true);
      }
    } else {
      logger.error("Couldn't open up the event edit view in GCal");
    }
  };

  const setFormValidationErrors = (errorMap: ReturnType<typeof validateForm>) => {
    if (errorMap.error) {
      const scrollToRef = (ref: React.MutableRefObject<HTMLDivElement | null>) =>
        ref.current?.offsetTop && window.scrollTo(0, ref.current?.offsetTop);

      scrollToRef(eventSchedulerContainer);

      setTitleError(errorMap.titleError);
      setTimeError(errorMap.timeError);
    }
  };

  const validateFormAndSetError = (): boolean => {
    const errorMap = validateForm();
    if (errorMap.error) {
      setFormValidationErrors(errorMap);
    }

    return errorMap.error;
  };

  const scheduleEvent = (goToEdit?: boolean) => {
    if (validateFormAndSetError()) {
      return;
    }

    setSavingEvent(!goToEdit);
    setOpeningInGCal(!!goToEdit);
    const org = getCurrentOrg(viewer);
    if (!org) {
      onScheduleFail(new Error("Failed to get org to schedule event"));
      return;
    }

    const timeZone = getRenderTimeZone();
    const startTimeRange = selectedSlot && selectedSlot.timeRange;

    if (!startTimeRange) {
      onScheduleFail(new Error("Missing start timeRange"));
      return;
    }

    const attendees = [...calendarIds, ...roomIds];
    const input: ScheduleNewEventV2MutationInput = {
      attendees,
      timeZone,
      timeRange: startTimeRange,
      location,
      description,
      title,
      conflictResolutionDefragId,
      conferenceType,
      orgRelayId: org.id,
      calendarId: org.primaryOrgCalendar,
      recurrenceOption: recurrenceOption ? recurrenceOption : ISchema.RecurrenceOption.None,
    };

    scheduleNewEvent(
      relay.environment,
      input,
      (payload?: ISchema.IScheduleNewEventV2MutationPayload) =>
        onScheduleSuccess(payload, goToEdit, org.primaryOrgCalendar),
      onScheduleFail,
    );
  };

  const scheduleMakeItWorkEvent = () => {
    track(TrackingEvents.EVENT_SCHEDULER.SCHEDULE_BY_MOVING.USER_CLICKED_FIX_IT_NOW, {
      slot: selectedSlot,
      attendees: calendarIds,
    });
    toast.success(
      "Sorry this feature may be coming soon! But please keep pressing it when it would be useful \
      for you because you're helping us track how often it helps out everyday. \
      For now, use the 'Leave the conflict' option to schedule.",
    );
    setUserClickedFixItNow(true);
    return;
  };

  const leaveTheConflict = () => {
    if (savingEvent) {
      return;
    }

    if (!userClickedFixItNow) {
      track(TrackingEvents.EVENT_SCHEDULER.SCHEDULE_BY_MOVING.USER_CLICKED_LEAVE_THE_CONFLICT, {
        slot: selectedSlot,
        attendees: calendarIds,
      });
    }
    scheduleEvent();
  };

  if (showMakeItWorkCard) {
    return (
      <div className="cw-flex cw-h-full cw-justify-center cw-items-center cw-bg-neutral-emphasis-hover">
        <MakeItWorkCard
          selectedSlot={selectedSlot}
          onClickFixItNow={scheduleMakeItWorkEvent}
          onClickScheduleAndLeaveTheConflict={leaveTheConflict}
          isSaving={savingEvent}
        />
      </div>
    );
  }

  return (
    <div ref={eventSchedulerContainer} style={s.container}>
      {!props.reschedulerEvent && !sessionStorage.getItem("inClockwiseHome") && (
        <ChromeNavEventScheduler />
      )}
      <div className="cw-flex-grow cw-h-full">
        <div className="cw-mt-1 cw-mb-3 cw-mr-2">
          <FormControl fullWidth error={!!titleError}>
            <TextField
              fieldSize="large"
              autoFocus={focusEventTitle}
              value={title}
              onChange={onTitleChange}
              placeholder="Meeting title"
              cw-id="add-event-title-input"
            />
            {titleError ? <FormHelperText>{titleError}</FormHelperText> : ""}
          </FormControl>
        </div>
        <div>
          <Divider style={s.divider} />
        </div>
        <EventSchedulerAttendeeInput
          orgId={org.id}
          personsByCalendarIdsErrorable={org.personsByCalendarIdsErrorable}
          onChange={handleAttendeeChange}
        />
        <Divider style={s.divider} />
        <EventSchedulerTimeInputRenderer
          {...schedulerQueryInput}
          range={range}
          specifiedDate={specifiedDate}
          selectedSlot={selectedSlot}
          onChange={onTimeChange}
          open={!useAutoSchedule} // this opens on init so people have to pick a time first
          persons={persons}
          timeError={timeError}
          useAutoSchedule={useAutoSchedule}
          isLoading={false}
          showCloseButton={true}
        />
        <Divider style={s.divider} />
        {includeRoom !== RoomOptionEnum.WithoutRoom ? (
          <EventSchedulerRoomInputRenderer
            {...schedulerQueryInput}
            selectedSlot={selectedSlot}
            roomIds={roomIds}
            onChange={onRoomChange}
            open={openRoomInput}
            useAutoSchedule={useAutoSchedule}
          />
        ) : (
          ""
        )}
        {includeRoom !== RoomOptionEnum.WithoutRoom ? <Divider style={s.divider} /> : ""}
        {maybeRenderVideoConferencingInput()}
        <div style={{ padding: "15px 0" }}>
          <AutopilotFormSidebarScheduler
            org={org}
            selectedDate={new ZonedMoment(specifiedDate || undefined).format("YYYY-MM-DD")}
            showRoom={includeRoom !== RoomOptionEnum.WithoutRoom}
            flex={flex}
            allowSimilarRooms={!!allowSimilarRooms}
            timeRange={{
              allowedStart: allowedStart,
              allowedEnd: allowedEnd,
            }}
            dayOnOffMap={dayOnOffMap}
            onChange={onAutopilotChange}
            warning={maybeGetWarning(viewer, calendarIds)}
          />
        </div>
        <Divider style={s.divider} />
        <EventSchedulerDescriptionInput onChange={onDescriptionChange} description={description} />
        <Divider style={s.divider} />
        <EventSchedulerLocationInput onChange={onLocationChange} location={location} />
      </div>
      <Divider style={s.divider} />
      <div className="cw-flex cw-flex-col cw-gap-2" style={s.buttonContainer}>
        <Button
          sentiment="positive"
          onClick={() => {
            if (selectedSlotIsMakeItWorkSlot) {
              if (validateFormAndSetError()) {
                return;
              }

              setShowMakeItWorkCard(true);
              return;
            }

            scheduleEvent();
          }}
          disabled={savingEvent}
          fullWidth
          cw-id="schedule-button-event-scheduler"
        >
          {savingEvent ? "Scheduling" : "Schedule"}
        </Button>
        {isWebExtension && (
          <Button
            onClick={() => scheduleEvent(true)}
            disabled={openingInGCal}
            fullWidth
            cw-id="schedule-edit-button-event-scheduler"
          >
            {!openingInGCal ? "Schedule & Edit" : "Opening in calendar"}
          </Button>
        )}
      </div>
    </div>
  );
};

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    calendarSelectionStates: state.webExtension.calendarSelectionStates,
    calendarIds: state.feedScheduler.calendarIds,
    roomRequired: state.feedScheduler.roomRequired,
    schedulingSessionId: state.feedScheduler.schedulingSessionId,
    reschedulerEvent: state.feedScheduler.reschedulerEvent,
    useAutoSchedule: state.feedScheduler.useAutoSchedule,
    minutesDuration: state.feedScheduler.minutesDuration,
    range: state.feedScheduler.range,
    specifiedDate: state.feedScheduler.specifiedDate,
    isWebExtension: state.webExtension.isWebExtension,
  };
}

const EventSchedulerRedux = connect(mapStateToProps, {
  setActiveViewDay,
  setActiveViewFeed,
  setSelectedMoment,
  setSnapToSelectedMomentOnNextMount,
  makeEventSchedulerViewActive,
  updateFeedSchedulerInput,
  updateEventSchedulerHasUnsavedChanges,
  updateScheduledEventIds,
})(EventSchedulerCmpt);

//////////////////
// RELAY
//////////////////
export const EventScheduler = createRefetchContainer<IContainer>(
  EventSchedulerRedux,
  eventSchedulerFragments,
  eventSchedulerQuery,
);
